/**
 * Calculs purs des favoris de formulaires (sans MobX ni appel réseau), même contrat que les applis mobiles :
 * - liste courante : favoris affichés par le client ;
 * - liste de référence : dernière liste renvoyée par le serveur, undefined si jamais reçue.
 * Le serveur fusionne : ajouts = courante - référence, retraits = référence - courante.
 * Les deux listes sont conservées dans le navigateur, par utilisateur, pour fonctionner hors connexion.
 */

/** Identifiant de formulaire normalisé pour les comparaisons (le serveur renvoie des Guid en minuscules) */
export const normalizeFormId = (formId: string | undefined | null): string => (formId ?? '').trim().toLowerCase();

/** UPN normalisé (même règle que les applis mobiles : sans espaces, en minuscules) */
export const normalizeUserUPN = (userUPN: string | undefined | null): string => (userUPN ?? '').trim().toLowerCase();

/** Identifiants normalisés, sans vide ni doublon, dans l'ordre d'origine */
export const normalizeFormIds = (formIds: (string | undefined | null)[] | undefined | null): string[] => {
  const result: string[] = [];
  for (const formId of formIds ?? []) {
    const id = normalizeFormId(formId);
    if (id !== '' && !result.includes(id)) result.push(id);
  }
  return result;
};

/** Indique si deux listes d'identifiants normalisés contiennent les mêmes formulaires, quel que soit l'ordre */
export const sameFormIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * Indique si la liste courante porte des changements que le serveur ne connaît pas encore.
 * Sans référence (jamais synchronisé), toute liste non vide est à envoyer.
 */
export const hasPendingFavoriteChanges = (current: string[], base: string[] | undefined): boolean =>
  base === undefined ? current.length > 0 : !sameFormIds(current, base);

/**
 * Nouvelle liste courante après réception de la liste fusionnée du serveur.
 * Les favoris ajoutés ou retirés depuis l'envoi de la requête sont conservés : ils partiront à l'appel suivant.
 * courante = (serveur + ajoutés depuis l'envoi) - retirés depuis l'envoi ; la référence devient la liste du serveur.
 *
 * @param current liste courante au moment de la réponse
 * @param sent liste courante au moment de l'envoi
 * @param server liste fusionnée renvoyée par le serveur
 */
export const mergeServerFavorites = (current: string[], sent: string[], server: string[]): string[] => {
  const result = normalizeFormIds(server);
  for (const id of current) {
    if (!sent.includes(id) && !result.includes(id)) result.push(id);
  }
  return result.filter((id) => !(sent.includes(id) && !current.includes(id)));
};

/** Favoris conservés dans le navigateur pour un utilisateur : liste courante et liste de référence */
export type StoredFavorites = {
  current: string[];
  base: string[] | undefined;
};

// Version du format conservé dans le navigateur
const STORED_FAVORITES_VERSION = 1;

/** Sérialise les favoris d'un utilisateur pour le stockage navigateur */
export const serializeStoredFavorites = ({ current, base }: StoredFavorites): string =>
  JSON.stringify({ version: STORED_FAVORITES_VERSION, current, base: base ?? null });

/**
 * Relit les favoris conservés dans le navigateur.
 * @returns undefined si rien n'est conservé ou si la valeur est illisible (la liste est alors relue du serveur)
 */
export const parseStoredFavorites = (raw: string | null | undefined): StoredFavorites | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.version !== STORED_FAVORITES_VERSION || !Array.isArray(parsed.current)) return undefined;
    return {
      current: normalizeFormIds(parsed.current),
      base: Array.isArray(parsed.base) ? normalizeFormIds(parsed.base) : undefined,
    };
  } catch {
    return undefined;
  }
};
